import { useEffect, useState } from "react";
import { dvr, dvrAvailable, formatBytes, type DvrVolume } from "../dvr/dvr.js";
import { usePlayerSettings } from "../player/playerSettingsStore.js";
import { requireFeature } from "../library/proFeatures.js";

/** The longest buffer kept (ADR 2026-09-27: up to 60 minutes). */
const MAX_MINUTES = 60;

/**
 * Minutes of buffer a drive has room for at this bitrate: what is free less the
 * buffer's reserve, which is three quarters of the DVR's (TimeshiftBuffer.java
 * budgetBytes), so a recording always has room first.
 */
export function bufferMinutes(volume: DvrVolume, bps: number): number {
  const room = volume.freeBytes - (volume.reserveBytes / 4) * 3;
  if (room <= 0 || bps <= 0) return 0;
  return Math.min(MAX_MINUTES, Math.floor(room / (bps / 8) / 60));
}

/**
 * Settings → Player: pause and rewind live TV, on by default, and the drive the
 * buffer is kept on, chosen from the same list as recordings (owner, 2026-10-07:
 * "the same option for storage ... as recording").
 */
export function TimeshiftSettings() {
  const enabled = usePlayerSettings((s) => s.timeshift);
  const setEnabled = usePlayerSettings((s) => s.setTimeshift);
  const volumeId = usePlayerSettings((s) => s.timeshiftVolumeId);
  const setVolumeId = usePlayerSettings((s) => s.setTimeshiftVolumeId);
  const [volumes, setVolumes] = useState<DvrVolume[]>([]);
  const [hdBps, setHdBps] = useState(6_250_000);

  useEffect(() => {
    if (!dvrAvailable()) return;
    let live = true;
    void dvr
      .getState()
      .then((state) => {
        if (!live) return;
        setVolumes(state.volumes);
        if (state.assumedBps > 0) setHdBps(state.assumedBps);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  // A drive that was chosen and then unplugged: the buffer falls back to the device.
  const chosen = volumes.find((v) => v.id === volumeId) ?? volumes[0];

  return (
    <>
      <div className="settings-row">
        <label className="settings-row-label" htmlFor="timeshift">
          Pause and rewind live TV
        </label>
        <input
          id="timeshift"
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            // Turning it ON is Pro; turning it off is always allowed.
            if (e.target.checked && !requireFeature("timeshift")) return;
            setEnabled(e.target.checked);
          }}
          aria-label="Save live TV while you watch so you can pause, rewind and fast-forward"
        />
      </div>
      {enabled && volumes.length > 0 && (
        <div className="settings-row">
          <label className="settings-row-label" htmlFor="timeshift-volume">
            Keep the live TV buffer on
          </label>
          <select
            id="timeshift-volume"
            className="library-select"
            value={chosen?.id ?? ""}
            onChange={(e) => setVolumeId(e.target.value === volumes[0]?.id ? undefined : e.target.value)}
            aria-label="Where to keep the live TV buffer"
          >
            {volumes.map((volume) => (
              <option key={volume.id} value={volume.id}>
                {volume.label} ({formatBytes(volume.freeBytes)} free)
              </option>
            ))}
          </select>
        </div>
      )}
      <p className="settings-row-hint">
        Saves the channel you are watching, up to an hour, so you can pause, rewind and fast-forward
        it. It starts again when you change channel, never counts as a recording, and gives way to
        recordings when space runs low.
        {enabled && chosen && (
          <>
            {" "}
            {bufferMinutes(chosen, hdBps) >= 5
              ? `${chosen.label} has room for about ${bufferMinutes(chosen, hdBps)} minutes of HD (longer for SD).`
              : `${chosen.label} is almost full, so live TV may play without rewind. A USB drive gives the full hour.`}
          </>
        )}
      </p>
    </>
  );
}
